import { type ChatEntry, EMOTES, LIMITS, type Seat, describeLog, describeResult } from '@euchre/core';
import { h, replace } from '../dom.js';
import { app, rerender, send } from '../state.js';
import { seatName, teamName } from './names.js';

let root: HTMLElement | null = null;
let tabsEl: HTMLElement;
let chatPane: HTMLElement;
let chatList: HTMLElement;
let logPane: HTMLElement;
let scorePane: HTMLElement;
let renderedChatIds = new Set<number>();
let renderedRoom = '';

function chatLine(e: ChatEntry): HTMLElement {
  if (!e.from && e.tone === 'vegas') return h('li', { class: 'chat-line heckle' }, h('span', { class: 'who dealer', text: 'Dealer' }), ' ', h('span', { text: e.text }));
  if (!e.from) return h('li', { class: 'chat-line system', text: e.text });
  const room = app.room;
  const who = room && e.from.playerId === room.you.playerId ? 'You' : e.from.name;
  const team = e.from.seat === null ? 'watcher' : `team-${e.from.seat % 2}`;
  return h('li', { class: 'chat-line' }, h('span', { class: `who ${team}`, text: who }), ' ', h('span', { text: e.text }));
}

export function sidebar(): HTMLElement {
  if (!root) build();
  update();
  return root!;
}

function build(): void {
  const input = h('input', {
    class: 'field chat-input',
    placeholder: 'Say something to the table',
    maxLength: LIMITS.chatMax,
    autocomplete: 'off',
  });
  const form = h(
    'form',
    {
      class: 'chat-form',
      onsubmit: (ev) => {
        ev.preventDefault();
        const text = input.value.trim();
        if (!text) return;
        send({ type: 'chat', text });
        input.value = '';
      },
    },
    input,
    h('button', { class: 'btn small', type: 'submit', text: 'Send' }),
  );
  const emoteRow = h(
    'div',
    { class: 'emotes', attrs: { role: 'group', 'aria-label': 'Reactions' } },
    EMOTES.map((emote) => h('button', { class: 'emote-btn', type: 'button', text: emote, title: `React ${emote}`, onclick: () => send({ type: 'emote', emote }) })),
  );
  chatList = h('ul', { class: 'chat-list', attrs: { 'aria-live': 'polite' } });
  chatPane = h('div', { class: 'pane chat-pane' }, chatList, emoteRow, form);
  logPane = h('div', { class: 'pane log-pane' });
  scorePane = h('div', { class: 'pane score-pane' });
  tabsEl = h('div', { class: 'tabs', attrs: { role: 'tablist' } });
  // On the table screen the sidebar is a panel that slides in; this header (hidden elsewhere) closes it.
  const head = h(
    'div',
    { class: 'drawer-head' },
    h('h2', { text: 'Table talk & records' }),
    h('button', {
      class: 'btn small drawer-close',
      type: 'button',
      text: 'Close',
      attrs: { 'aria-label': 'Close panel' },
      onclick: () => {
        app.ui.drawerOpen = false;
        rerender();
      },
    }),
  );
  root = h('aside', { class: 'sidebar', id: 'side-panel', attrs: { 'aria-label': 'Chat, hand log and score sheet' } }, head, tabsEl, chatPane, logPane, scorePane);
}

function update(): void {
  const room = app.room;
  if (!room) return;
  if (renderedRoom !== room.code) {
    renderedRoom = room.code;
    renderedChatIds = new Set();
    chatList.replaceChildren();
  }
  // Chat is append-only so typing and scrolling aren't disturbed.
  const nearBottom = chatList.scrollHeight - chatList.scrollTop - chatList.clientHeight < 40;
  for (const e of app.chat) {
    if (renderedChatIds.has(e.id)) continue;
    renderedChatIds.add(e.id);
    if (e.tone === 'vegas' && app.ui.dealerTalk !== 'vegas') continue; // Plain dealer talk: no heckles
    chatList.appendChild(chatLine(e));
  }
  while (chatList.children.length > 120) chatList.firstElementChild?.remove();
  if (nearBottom) chatList.scrollTop = chatList.scrollHeight;

  const tab = app.ui.tab;
  if (tab === 'chat' && app.ui.drawerOpen) app.unreadChat = 0;
  const tabBtn = (id: typeof tab, label: string) =>
    h('button', {
      class: `tab${tab === id ? ' active' : ''}`,
      type: 'button',
      text: label,
      attrs: { role: 'tab', 'aria-selected': String(tab === id) },
      onclick: () => {
        app.ui.tab = id;
        rerender();
      },
    });
  replace(
    tabsEl,
    tabBtn('chat', app.unreadChat ? `Chat (${app.unreadChat})` : 'Chat'),
    tabBtn('log', 'Hand log'),
    tabBtn('scores', 'Score sheet'),
  );
  chatPane.hidden = tab !== 'chat';
  logPane.hidden = tab !== 'log';
  scorePane.hidden = tab !== 'scores';

  const nameOf = (s: Seat) => seatName(room, s);
  const teamOf = (t: 0 | 1) => teamName(room, t);
  const mySeat = room.you.seat;
  const shortTeam = (t: 0 | 1) => (mySeat === null ? `Team ${t + 1}` : mySeat % 2 === t ? 'Us' : 'Them');
  if (tab === 'log') {
    const g = room.game;
    if (!g) {
      replace(logPane, h('p', { class: 'empty', text: 'The log fills in once the cards are dealt.' }));
    } else {
      const current = g.log.map((e) => h('li', { class: `log-${e.t}`, text: describeLog(e, nameOf, teamOf) }));
      const prev = g.previousHandLog.map((e) => h('li', { class: `log-${e.t}`, text: describeLog(e, nameOf, teamOf) }));
      replace(
        logPane,
        h('h3', { text: `Hand ${g.handNumber}` }),
        h('ol', { class: 'log-list' }, current),
        prev.length ? [h('h3', { text: 'Previous hand' }), h('ol', { class: 'log-list past' }, prev)] : null,
      );
    }
  }
  if (tab === 'scores') {
    const g = room.game;
    const rows = (g?.history ?? []).map((s) =>
      h(
        'tr',
        null,
        h('td', { text: String(s.handNumber) }),
        h('td', { text: describeResult(s.result, nameOf, teamOf) }),
        h('td', { class: 'num', text: String(s.scoresAfter[0]) }),
        h('td', { class: 'num', text: String(s.scoresAfter[1]) }),
      ),
    );
    replace(
      scorePane,
      h(
        'p',
        { class: 'games-won' },
        `Games this session — ${teamOf(0)}: ${room.gamesWon[0]}, ${teamOf(1)}: ${room.gamesWon[1]}`,
      ),
      rows.length
        ? h(
            'table',
            { class: 'score-sheet' },
            h(
              'thead',
              null,
              h(
                'tr',
                null,
                h('th', { text: 'Hand' }),
                h('th', { text: 'What happened' }),
                ([0, 1] as const).map((t) => h('th', { class: 'num', text: shortTeam(t), title: teamOf(t) })),
              ),
            ),
            h('tbody', null, rows),
          )
        : h('p', { class: 'empty', text: 'Scored hands will be listed here.' }),
    );
  }
}

export function resetSidebar(): void {
  renderedRoom = '';
}
